/**
 * Pipelines Page (Saved Pipelines)
 */

import React, { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { setStorageItem } from '@/lib/storageUtils';
import { useAppStore } from '@/store/useAppStore';
import { usePipelineStore } from '@/store/usePipelineStore';

export const PipelinesPage: React.FC<{ onNavigate: (to: string) => void }> = ({
  onNavigate,
}) => {
  const savedPipelines = usePipelineStore((s) => s.savedPipelines);
  const deletePipeline = usePipelineStore((s) => s.deletePipeline);
  const { loadPipeline } = useAppStore();

  const sorted = useMemo(() => {
    return [...savedPipelines].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [savedPipelines]);

  const openPipeline = (pipelineId: string) => {
    const pipeline = usePipelineStore.getState().getPipeline(pipelineId);
    if (!pipeline) return;

    loadPipeline(pipeline);

    setStorageItem('app.selectedToolId', null);
    onNavigate('/');
  };

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-6xl mx-auto p-8">
        <h1 className="text-3xl font-semibold">Saved Pipelines</h1>

        <div className="mt-8">
          {sorted.length === 0 ? (
            <div className="text-sm text-muted-foreground">No saved pipelines yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sorted.map((p) => (
                <div
                  key={p.id}
                  className="border border-border rounded-lg p-4 bg-muted/10"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{p.name}</div>
                      {p.description ? (
                        <div className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {p.description}
                        </div>
                      ) : null}
                      <div className="text-xs text-muted-foreground mt-2">
                        {p.steps.length} steps
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openPipeline(p.id)}
                      >
                        Open
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deletePipeline(p.id)}
                        title="Delete"
                      >
                        ✕
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
